import type { Difficulty, InteractiveId, LearningKnowledgeCheck, LearningLesson, LearningResource, LearningTrackId, LessonContentMeta } from "./types";

interface LessonInput {
  slug: string;
  title: string;
  minutes: number;
  difficulty?: Difficulty;
  learn: string[];
  explanation: string[];
  example: string;
  interactive?: InteractiveId;
  check: Omit<LearningKnowledgeCheck, "id">;
  takeaway: string;
  tool: LearningResource;
  resource: LearningResource;
  meta?: LessonContentMeta;
}

export const lessonsFor = (trackId: LearningTrackId, inputs: LessonInput[]): LearningLesson[] =>
  inputs.map((l) => ({
    ...l,
    id: `${trackId}.${l.slug}`,
    trackId,
    difficulty: l.difficulty ?? "Beginner",
    check: { ...l.check, id: `${trackId}.${l.slug}.check` },
  }));

/** Shorthand for a true/false check. */
export const tf = (question: string, answer: boolean, explanation: string): Omit<LearningKnowledgeCheck, "id"> => ({
  type: "true-false",
  question,
  options: [
    { id: "true", label: "True" },
    { id: "false", label: "False" },
  ],
  correct: [answer ? "true" : "false"],
  explanation,
});

/** Shorthand for a single/multi/scenario check. Mark correct options with a leading "*". */
export const mc = (
  type: "single" | "multi" | "scenario",
  question: string,
  options: string[],
  explanation: string,
): Omit<LearningKnowledgeCheck, "id"> => {
  const opts = options.map((o, i) => ({ id: String.fromCharCode(97 + i), label: o.replace(/^\*/, "") }));
  return {
    type,
    question,
    options: opts,
    correct: options.map((o, i) => (o.startsWith("*") ? opts[i].id : null)).filter(Boolean) as string[],
    explanation,
  };
};
